import { GameStatus } from '@draughts/shared';

/**
 * «La schermata che sto guardando ha una partita VIVA sotto?»
 *
 * Un chiamante solo, oggi: la conferma d'uscita del gesto indietro
 * (`isGameInProgress` in App, che la interroga dentro il gestore `popstate`).
 *
 * Ne aveva due: nascondeva anche «Studia in editor» nella revisione. Quel
 * bottone e' diventato «Analizza partita» e non naviga piu', quindi il suo gate
 * si e' allargato — adesso usa `laSchermataUsaLoStoreDiPartita` qui sotto, e il
 * perche' sta scritto la'.
 *
 * Sta qui fuori, pura e con gli stati passati come argomenti, perche' chi la
 * interroga dal gesto indietro li legge con `getState()` (non puo' essere
 * reattivo) mentre chi la interrogava dalla revisione li leggeva con dei
 * selettori (DOVEVA esserlo). La regola invece e' una, e scritta una volta.
 *
 * ⚠️ **E' chiavata sulla SCHERMATA, e non va trasformata in un «c'e' una partita
 * in corso» globale.** `leaveGameTo` naviga e non azzera `game-store`: chi lascia
 * a meta' una partita contro il computer si porta dietro uno `status` a
 * `'PLAYING'` a tempo indeterminato (lo riazzerano solo una partita nuova e il
 * logout). Un predicato globale spegnerebbe «Studia in editor» in TUTTA
 * l'applicazione dopo una partita locale abbandonata. La review vive sempre
 * dentro la schermata corrente, quindi la schermata e' esattamente il contesto
 * che serve.
 *
 * ⚠️ E non va **stretta** (per esempio aggiungendo `'ended'` fra gli stati
 * online): da qui passa anche la conferma d'uscita, e stringere qui vorrebbe
 * dire chiedere «vuoi davvero uscire?» a chi sta solo guardando un riepilogo.
 */
export interface StatiDiPartita {
  /** `useGameStore.status` — la partita locale (contro il computer o in due sullo stesso schermo). */
  locale: string;
  /** `useMultiplayerStore.status`. */
  online: string;
  /** `useTournamentStore.status`. */
  torneo: string;
  /** `useArenaStore.status`. */
  arena: string;
}

export function laPartitaDellaSchermataEInCorso(gameScreen: string, stati: StatiDiPartita): boolean {
  switch (gameScreen) {
    case 'local-game':
      return stati.locale === GameStatus.PLAYING;
    case 'online-game':
      return stati.online === 'playing';
    case 'tournament-game':
      return stati.torneo === 'playing';
    case 'arena-game':
      // 'gameEnd' è ancora dentro la partita: il riepilogo col punteggio non
      // se ne va da solo, e uscirne è una scelta da confermare come quella di
      // abbandonare una partita. Chi esce comunque non resta a piedi: a fine
      // partita si è in pausa, e la banda dell'arena mostra «Riprendi».
      return stati.arena === 'playing' || stati.arena === 'matchFound' || stati.arena === 'gameEnd';
    default:
      return false;
  }
}

/**
 * «La schermata sotto DISEGNA dallo store di partita?»
 *
 * Fratello piu' largo di `laPartitaDellaSchermataEInCorso`, e la larghezza e' il
 * punto. L'analisi aperta dalla revisione (il bottone «Analizza partita») vive su
 * `game-store` e lo RISCRIVE con `startLocalGameFromBoard`, ma — al contrario
 * dell'analisi aperta dall'editor — non naviga: la schermata sotto resta montata.
 * Quindi qui non conta se la partita e' viva, conta se c'e' ancora qualcuno che
 * disegna da quello store.
 *
 * Il caso che l'ha richiesto e' questo: a partita online FINITA il riepilogo
 * resta a schermo, da li' si apre il profilo dell'avversario
 * (`GameEndOverlay` -> `PlayerProfileOverlay`), dal profilo una sua partita in
 * revisione, e «Analizza partita» riscriverebbe lo store che la damiera dietro
 * sta ancora leggendo — chiuso tutto, la partita appena giocata mostrerebbe la
 * linea d'analisi di un'altra. `laPartitaDellaSchermataEInCorso` non lo vede:
 * per lei `online: 'ended'` e' `false`, e giustamente.
 *
 * ⚠️ **Le due non si fondono.** Quella sopra alimenta anche la conferma d'uscita
 * del gesto indietro, e allargarla li' vorrebbe dire chiedere «vuoi davvero
 * uscire?» a chi sta solo guardando un riepilogo — c'e' gia' scritto nel suo
 * commento, ed e' la ragione per cui questa nasce accanto e non al suo posto.
 *
 * Le tre schermate dei problemi ci stanno dentro per prudenza: la revisione non
 * e' raggiungibile da li', ma anche loro passano da `startLocalGameFromBoard`, e
 * costa meno elencarle che ricordarsene.
 */
const SCHERMATE_SU_GAME_STORE = new Set([
  'local-game',
  'online-game',
  'spectate',
  'arena-game',
  'tournament-game',
  'puzzle',
  'puzzle-invite',
  'puzzle-preview',
]);

export function laSchermataUsaLoStoreDiPartita(screen: string): boolean {
  return SCHERMATE_SU_GAME_STORE.has(screen);
}
